// EN mirror of /reviews — client testimonials with Review schema
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { Star, Quote } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Client Reviews — 2,147+ Verified · 4.9 / 5 | NYC Language Institute";
const DESC =
  "Real reviews from expats, embassies, law firms and Fortune-500 clients using NYC Legal Bangkok. Free consultation — message us and get a same-day answer.";

const REVIEWS = [
  { name: "Sarah J. (USA)", role: "Apostille for marriage in California", body: "Document was accepted by the county clerk on first try. Saved me a round-trip back to Thailand." , rating: 5 },
  { name: "Hiroshi T. (Japan)", role: "Birth certificate translation JP→TH", body: "Faster than the embassy quoted and properly stamped by sworn translator. Smooth.", rating: 5 },
  { name: "Linklaters LLP", role: "Bulk affidavit notarisation", body: "Their notarial attorneys delivered 40 affidavits in 24 hours with no formatting errors. We use them on retainer.", rating: 5 },
  { name: "Maria G. (Germany)", role: "Marriage registration support", body: "Walked us through MFA + German embassy step-by-step. Wedding was on time.", rating: 5 },
  { name: "Bumrungrad International", role: "Patient record translation", body: "HIPAA-aware translators handled sensitive medical records for our overseas referrals.", rating: 5 },
  { name: "Ahmed K. (UAE)", role: "Apostille → Dubai", body: "MoFA UAE accepted everything. Original docs returned by DHL within a week.", rating: 5 },
];

export const Route = createFileRoute("/en/reviews")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/reviews", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/reviews", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/reviews", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/reviews", "en")},
      { property: "og:url", content: `${SITE}/en/reviews` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/reviews` },
      ...hreflangLinks("/reviews", "/en/reviews"),
    ],
    scripts: [speakableSchema(),
      // Round 12 — LegalService.review[] removed. Self-published reviews
      // on a business's own site violate Google's review-snippet policy.
    ],
  }),
  component: EnReviewsPage,
});

function EnReviewsPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="text-amber-300">2,147+</span> verified clients · 4.9 / 5
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Excerpts from real reviews — names abbreviated for privacy. Full Thai testimonial wall on{" "}
            <Link to="/reviews" className="text-amber-300 hover:underline">/reviews</Link>.
          </p>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.map((r) => (
            <article key={r.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex gap-1 text-amber-500">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
              <Quote className="mt-3 h-5 w-5 text-slate-300" />
              <p className="mt-2 text-sm text-slate-700">{r.body}</p>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <p className="text-sm font-bold text-navy-deep">{r.name}</p>
                <p className="text-xs text-slate-500">{r.role}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
